import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge, Spinner } from "@learn-me/ui";
import { Plus, Edit, Trash2, FileText, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { formatCurrency } from "@learn-me/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  materialsApi,
  MATERIAL_CATEGORY_LABELS,
} from "@/features/materials/api/materialsApi";

export default function MaterialsPage() {
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const { data: materials, isLoading } = useQuery({
    queryKey: ["admin-materials"],
    queryFn: () => materialsApi.listAdmin(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => materialsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-materials"] });
      toast.success("Material eliminado");
      setToDelete(null);
    },
    onError: () => {
      toast.error("Error al eliminar el material");
      setToDelete(null);
    },
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Materiales Digitales</h1>
          <p className="text-sm text-muted-foreground">
            Vende lecturas, prácticas, manuales y guías descargables
          </p>
        </div>
        <Link to="/admin/materials/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Material
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : !materials || materials.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            No hay materiales. Crea el primero con el botón Nuevo Material.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {materials.map((m) => (
            <Card key={m.id}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium truncate">{m.title}</h3>
                    <Badge variant="outline" className="text-xs">
                      {MATERIAL_CATEGORY_LABELS[m.category]}
                    </Badge>
                    {m.isPublished ? (
                      <Badge className="text-xs bg-green-500">
                        <Eye className="h-3 w-3 mr-1" />
                        Publicado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        <EyeOff className="h-3 w-3 mr-1" />
                        Borrador
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {m.isFree
                      ? "Gratis"
                      : formatCurrency(Number(m.salePrice), m.currency)}
                    {m._count ? ` · ${m._count.purchases} compras` : ""}
                  </p>
                </div>
                <Link to={`/admin/materials/${m.id}/edit`}>
                  <Button variant="ghost" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => setToDelete({ id: m.id, title: m.title })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.id)}
        title="Eliminar material"
        description={`¿Seguro que deseas eliminar "${toDelete?.title ?? ""}"? Esta acción no se puede deshacer.`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
